import React, { FC, useEffect, useRef } from 'react';

/**
 * Atmospheric "archive dust" behind the Projects page (Aaron, 5 Nov 2026): fine grey motes drifting
 * slowly in a faint haze. Each mote sits at a depth; when you scroll, deeper motes move less than
 * nearer ones and all move slower than the page, so the background shifts with you and feels deep.
 *
 * Kept quiet on purpose: low contrast, behind everything, no clicks. It pauses when the tab is hidden,
 * scales the number of motes to the window, and stays still for people who ask for reduced motion.
 */

type Mote = { x: number; y: number; r: number; depth: number; alpha: number; vx: number; vy: number; phase: number; soft: boolean };

// Pink motes (Aaron, 5 Nov 2026), the brand pink ui.accent (#E5056A) as r,g,b; the alpha keeps them soft.
const INK = '229,5,106';

const DustBackground: FC = () => {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let w = 0;
    let h = 0;
    let motes: Mote[] = [];
    let raf = 0;
    let last = performance.now();

    const seed = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.min(220, Math.round((w * h) / 8000));
      motes = Array.from({ length: count }, () => {
        const depth = 0.15 + Math.random() * 0.85; // 1 = nearest
        const soft = Math.random() < 0.16; // some large out-of-focus motes
        return {
          x: Math.random() * w,
          y: Math.random() * h,
          r: soft ? 4 + Math.random() * 9 : 0.7 + depth * 2 * Math.random() + 0.4,
          depth,
          alpha: soft ? 0.07 + Math.random() * 0.08 : 0.18 + depth * 0.4 * Math.random() + 0.06,
          vx: (Math.random() - 0.5) * 6 * depth,
          vy: (-2 - Math.random() * 6) * depth, // drifting gently upward, like dust in a light beam
          phase: Math.random() * Math.PI * 2,
          soft,
        };
      });
    };

    const draw = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const scroll = window.scrollY || 0;
      ctx.clearRect(0, 0, w, h);

      // Faint haze from the top, like light falling into a store room.
      const haze = ctx.createRadialGradient(w * 0.5, -h * 0.2, 0, w * 0.5, -h * 0.2, Math.max(w, h) * 0.9);
      haze.addColorStop(0, 'rgba(255,255,255,0.35)');
      haze.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = haze;
      ctx.fillRect(0, 0, w, h);

      const pad = 12;
      for (const m of motes) {
        if (!still) {
          m.phase += dt * (0.4 + m.depth * 0.6);
          m.x += (m.vx + Math.sin(m.phase) * 3 * m.depth) * dt;
          m.y += m.vy * dt;
          if (m.x < -pad) m.x += w + pad * 2;
          if (m.x > w + pad) m.x -= w + pad * 2;
        }
        // Parallax: the page moves 1:1, motes move 10–55% of that, nearer ones more.
        const span = h + pad * 2;
        const yy = ((((m.y - scroll * (0.1 + m.depth * 0.45)) % span) + span) % span) - pad;
        const twinkle = still ? 1 : 0.75 + 0.25 * Math.sin(m.phase * 1.7);
        if (m.soft) {
          const g = ctx.createRadialGradient(m.x, yy, 0, m.x, yy, m.r);
          g.addColorStop(0, `rgba(${INK},${m.alpha * twinkle})`);
          g.addColorStop(1, `rgba(${INK},0)`);
          ctx.fillStyle = g;
        } else {
          ctx.fillStyle = `rgba(${INK},${m.alpha * twinkle})`;
        }
        ctx.beginPath();
        ctx.arc(m.x, yy, m.r, 0, Math.PI * 2);
        ctx.fill();
      }
      if (!still) raf = requestAnimationFrame(draw);
    };

    const start = () => {
      cancelAnimationFrame(raf);
      last = performance.now();
      raf = requestAnimationFrame(draw);
    };
    const onVisibility = () => (document.hidden ? cancelAnimationFrame(raf) : start());
    const onResize = () => {
      seed();
      if (still) draw(performance.now());
    };
    const onScroll = () => still && draw(performance.now());

    seed();
    start();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      style={{ position: 'fixed', top: 0, left: 0, zIndex: 0, pointerEvents: 'none', display: 'block' }}
    />
  );
};

export default DustBackground;
